// src/components/CheckoutPage.js - FIXED: Added QR Code section
import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  IconButton,
  TextField,
} from "@mui/material";
import {
  ArrowBack,
  Payment as PaymentIcon,
  ShoppingBag,
  CheckCircle,
  LocalShipping,
} from "@mui/icons-material";
import PackageOrderService from "../services/PackageOrderService";
import { confirmByCartOrderId } from "../services/CartOrderConfirmationService";
import CashfreeService from "../services/CashfreeService";
import PackageSuccessModal from "./PackageSuccessModal";
import ManualUpiPaymentBlock from "./ManualUpiPaymentBlock";
import { calculateDeliveryFee, getDeliveryMessage } from "../utils/validation";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import { CURRENT_ACTIVATION_FEE } from "../config/membershipConfig";
import { USE_CASHFREE_PAYMENTS } from "../config/paymentConfig";
import MerchantLegalNotice from "./MerchantLegalNotice";

const CheckoutPage = ({
  packageData,
  userProfile,
  isAdmin,
  onBack,
  onPaymentComplete,
  user,
}) => {
  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [paymentTransactionId, setPaymentTransactionId] = useState("");
  const [transactionIdError, setTransactionIdError] = useState("");
  const [orderData, setOrderData] = useState(null);

  const registrationFee = 499;

  // Check if user needs to pay registration fee
  const calculateRegistrationFee = () => {
    if (isAdmin) return 0;
    if (userProfile?.hasPurchasedPackage) return 0;
    if (packageData?.category === "Activation Fee") return 0;
    return 0; // Hidden from users
  };

  const isAutoFillUpgrade = packageData.isAutoFillUpgrade === true;

  // Calculate amounts - handle sub-items packages
  // For sub-items packages, finalTotal already includes GST + activation fee + meta whale fee
  const packagePrice = isAutoFillUpgrade
    ? packageData.price
    : packageData.isSubItemPackage
      ? packageData.finalTotal
      : packageData.price;
  const totalRegistrationFee = calculateRegistrationFee();

  // Get the selected sub-items array (selectedSubItemsList is the array, selectedSubItems is an object)
  const selectedSubItemsArray = packageData.selectedSubItemsList || [];

  // For sub-items packages, GST is already included in the prices
  const isSubItemsPackageWithGST =
    packageData.isSubItemPackage && selectedSubItemsArray.length > 0;
  
  // Activation fee for sub-item packages
  const activationFee = packageData.isSubItemPackage
    ? packageData.activationFee ?? CURRENT_ACTIVATION_FEE
    : 0;
  
  // Meta Whale fee if applicable
  const metaWhaleFee = packageData.metaWhaleFeeIncluded || 0;
  
  // Calculate subtotal before delivery fee
  const subtotalBeforeDelivery = packagePrice + totalRegistrationFee;
  // Subscriptions don't require delivery - only physical items do
  const requiresDelivery =
    !isAutoFillUpgrade &&
    packageData.isSubItemPackage &&
    selectedSubItemsArray.length > 0;
  const deliveryFee = requiresDelivery ? calculateDeliveryFee(subtotalBeforeDelivery, user?.email) : 0;
  const totalAmount = subtotalBeforeDelivery + deliveryFee;
  const deliveryMessage = requiresDelivery ? getDeliveryMessage(subtotalBeforeDelivery, user?.email) : { isFree: true, message: "No delivery required for subscriptions" };
  
  // Calculate GST total from selected items
  const calculateGSTTotal = () => {
    if (!isSubItemsPackageWithGST) return 0;
    return selectedSubItemsArray.reduce((total, item) => {
      return total + (item.gstAmount || 0);
    }, 0);
  };
  
  const includedGSTAmount = calculateGSTTotal();
  
  // Derive values from the total to ensure everything adds up:
  // packagePrice = itemsSubtotalWithGST + activationFee + metaWhaleFee
  // So: itemsSubtotalWithGST = packagePrice - activationFee - metaWhaleFee
  const itemsSubtotalWithGST = isSubItemsPackageWithGST
    ? packagePrice - activationFee - metaWhaleFee
    : packagePrice;
  
  // itemsSubtotal (without GST) = itemsSubtotalWithGST - GST
  const itemsSubtotal = itemsSubtotalWithGST - includedGSTAmount;

  // Calculate discount/savings - Enhanced for sub-items packages
  // Calculate sub-items totals WITHOUT GST for proper comparison
  const calculateSubItemsTotals = () => {
    if (packageData.isSubItemPackage && selectedSubItemsArray.length > 0) {
      let totalMRP = 0;
      let totalPackagePrice = 0; // Sum of itemPrice (without GST)

      selectedSubItemsArray.forEach((subItem) => {
        const quantity = subItem.selectedQuantity || 1;
        // Use compareAtPrice (MRP) if available, otherwise sellingPrice, otherwise itemPrice
        const mrpPrice =
          subItem.compareAtPrice ||
          subItem.sellingPrice ||
          subItem.itemPrice ||
          0;
        const packageItemPrice = subItem.itemPrice || 0;

        totalMRP += mrpPrice * quantity;
        totalPackagePrice += packageItemPrice * quantity;
      });

      return { totalMRP, totalPackagePrice };
    }
    return { totalMRP: 0, totalPackagePrice: 0 };
  };

  const subItemsTotalsRaw = calculateSubItemsTotals();

  const calculateTotalSavings = () => {
    if (packageData.isSubItemPackage && selectedSubItemsArray.length > 0) {
      const { totalMRP } = subItemsTotalsRaw;

      // Calculate savings: MRP vs (Items + GST)
      // Activation fee and Meta Whale fee are not part of savings
      // Use itemsSubtotalWithGST for exact consistency
      const savings = totalMRP - itemsSubtotalWithGST;
      const savingsPercentage =
        totalMRP > 0 ? Math.round((savings / totalMRP) * 100) : 0;

      return {
        originalPrice: totalMRP,
        currentPrice: itemsSubtotalWithGST, // Items with GST (excluding activation fee)
        moneySaved: savings > 0 ? savings : 0,
        discountPercentage: savingsPercentage > 0 ? savingsPercentage : 0,
        hasSavings: savings > 0,
      };
    } else {
      // For regular packages - compare base prices
      const originalPrice =
        packageData.compareAtPrice || packageData.originalPrice || 0;
      const currentPrice = packageData.price || 0;
      const moneySaved =
        originalPrice > currentPrice ? originalPrice - currentPrice : 0;
      return {
        originalPrice,
        currentPrice,
        moneySaved,
        discountPercentage:
          originalPrice > 0
            ? Math.round((moneySaved / originalPrice) * 100)
            : 0,
        hasSavings: moneySaved > 0,
      };
    }
  };

  const savingsInfo = calculateTotalSavings();
  const { originalPrice, moneySaved, discountPercentage, hasSavings } =
    savingsInfo;

  const needsRegistrationFee = totalRegistrationFee > 0;

  // Get sub-items specific data
  const isSubItemsPackage =
    packageData.isSubItemPackage && selectedSubItemsArray.length > 0;
  // subItemTotal from PackageStore now comes WITHOUT GST
  const subItemsTotal = packageData.subItemTotal || 0;

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const handleManualUpiPayment = async () => {
    const txn = paymentTransactionId.trim();
    if (txn.length < 6) {
      setTransactionIdError("Enter the UPI transaction ID from your payment app.");
      return;
    }
    setTransactionIdError("");
    setLoading(true);
    try {
      const orderResult = await PackageOrderService.createPackageOrder(
        packageData,
        userProfile,
        {
          method: "upi",
          amount: totalAmount,
          referenceNumber: txn,
          transactionDetails: txn,
        }
      );

      if (!orderResult.success) {
        throw new Error(orderResult.error);
      }

      await PackageOrderService.updateUserPendingPurchase(
        user.uid,
        packageData,
        orderResult.orderId
      );

      setOrderData({
        orderId: orderResult.orderId,
        packageId: packageData.id,
        packageName: packageData.name,
        packagePrice: packagePrice,
        registrationFee: totalRegistrationFee,
        totalAmount: totalAmount,
        purchaseDate: new Date().toISOString(),
        status: "pending",
        userHadPackageBefore: userProfile?.hasPurchasedPackage || false,
      });

      setSuccessModal(true);
    } catch (error) {
      console.error("Payment error:", error);
      alert(error.message || "Could not submit payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCashfreePayment = async () => {
    setLoading(true);
    try {
      const orderResult = await PackageOrderService.createPackageOrder(
        packageData,
        userProfile,
        {
          method: "cashfree",
          amount: totalAmount,
          includesRegistrationFee: needsRegistrationFee,
          status: "pending_payment",
        }
      );

      if (!orderResult.success) {
        throw new Error(orderResult.error);
      }

      const paymentResult = await CashfreeService.initiatePayment(
        {
          amount: totalAmount,
          description: `${packageData.name} Purchase`,
          orderId: orderResult.orderId,
        },
        userProfile
      );

      if (!paymentResult.success) {
        throw new Error(paymentResult.error || "Payment failed. Please try again.");
      }

      const updateResult = await PackageOrderService.updateOrderPaymentDetails(
        orderResult.orderId,
        {
          cashfreeOrderId: paymentResult.orderId,
          cashfreePaymentId: paymentResult.paymentId,
          transactionDetails: paymentResult.paymentId,
          status: "payment_completed",
        }
      );

      if (!updateResult.success) {
        throw new Error(updateResult.error);
      }

      await PackageOrderService.updateUserPendingPurchase(
        user.uid,
        packageData,
        orderResult.orderId
      );

      const confirmResult = await confirmByCartOrderId(orderResult.orderId, {
        confirmedBy: "cashfree",
      });
      if (!confirmResult.success) {
        throw new Error(
          confirmResult.error ||
            "Payment received but order confirmation failed. Contact support with your order ID."
        );
      }

      setOrderData({
        orderId: orderResult.orderId,
        packageId: packageData.id,
        packageName: packageData.name,
        packagePrice: packagePrice,
        registrationFee: totalRegistrationFee,
        totalAmount: totalAmount,
        purchaseDate: new Date().toISOString(),
        status: "confirmed",
        userHadPackageBefore: userProfile?.hasPurchasedPackage || false,
      });

      setSuccessModal(true);
    } catch (error) {
      console.error("Payment error:", error);
      alert(error.message || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (USE_CASHFREE_PAYMENTS) {
      handleCashfreePayment();
    } else {
      handleManualUpiPayment();
    }
  };

  const handleSuccessModalClose = () => {
    setSuccessModal(false);
    if (orderData) {
      onPaymentComplete(orderData);
    }
  };

  const handleContinueShopping = () => {
    setSuccessModal(false);
    onBack();
  };


  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Container maxWidth="md" sx={{ py: 4 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              ...glassStyles,
              p: { xs: 2, sm: 3 },
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <IconButton
              onClick={onBack}
              sx={{
                mr: { xs: 1, sm: 2 },
                bgcolor: primaryBlue[500],
                color: "white",
                "&:hover": {
                  bgcolor: primaryBlue[600],
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant={{ xs: "h5", sm: "h4" }}
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Package Checkout
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              ...glassStyles,
              p: { xs: 2, sm: 3, md: 4 },
              bgcolor: "rgba(255, 255, 255, 0.5)",
            }}
          >
            {/* Package Details */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <ShoppingBag color="primary" sx={{ mr: 1 }} />
                <Typography
                  variant={{ xs: "h6", sm: "h5" }}
                  sx={{
                    color: primaryBlue[700],
                    fontWeight: 600,
                  }}
                >
                  Order Summary
                </Typography>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(15px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "20px",
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "flex-start" },
                      mb: 2,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant={{ xs: "h6", sm: "h6" }}
                      sx={{ color: primaryBlue[700], fontWeight: 600 }}
                    >
                      {packageData.name}
                    </Typography>
                    <Chip
                      label={packageData.category}
                      size="small"
                      sx={{
                        bgcolor: primaryBlue[100],
                        color: primaryBlue[700],
                        fontWeight: 500,
                      }}
                    />
                  </Box>

                  {isSubItemsPackage ? (
                    <>
                      {/* Sub-items Package Display */}
                      <Typography
                        variant="subtitle1"
                        sx={{ color: primaryBlue[700], fontWeight: 600, mb: 2 }}
                      >
                        Selected Items:
                      </Typography>

                      <Box sx={{ mb: 3 }}>
                        {selectedSubItemsArray.map((item, index) => {
                          const mrpPrice =
                            item.compareAtPrice ||
                            item.sellingPrice ||
                            item.itemPrice ||
                            0;
                          const packageItemPrice = item.itemPrice || 0;
                          // GST is already calculated and included for each item from PackageStore
                          const itemGstAmount = item.gstAmount || 0;
                          const itemPriceWithGST =
                            item.totalWithGST || (packageItemPrice + itemGstAmount);
                          // Savings = MRP - (selling price + GST)
                          const itemSavings = mrpPrice - itemPriceWithGST;
                          const itemHasSavings = itemSavings > 0;
                          const itemDiscountPercent =
                            mrpPrice > 0
                              ? Math.round((itemSavings / mrpPrice) * 100)
                              : 0;

                          return (
                            <Box
                              key={index}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                bgcolor: "rgba(0, 114, 206, 0.1)",
                                borderRadius: 1,
                                border: `1px solid ${primaryBlue[200]}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: primaryBlue[800],
                                      fontWeight: 600,
                                    }}
                                  >
                                    {item.itemName}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      mt: 0.5,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      {formatPrice(packageItemPrice)}
                                    </Typography>
                                    {itemHasSavings && (
                                      <>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "text.secondary",
                                            textDecoration: "line-through",
                                            fontSize: "0.8rem",
                                          }}
                                        >
                                          MRP: {formatPrice(mrpPrice)}
                                        </Typography>
                                        <Chip
                                          label={`${itemDiscountPercent}% OFF`}
                                          size="small"
                                          sx={{
                                            bgcolor: "rgba(76, 175, 80, 0.15)",
                                            color: "#2e7d32",
                                            fontWeight: 600,
                                            fontSize: "0.7rem",
                                            height: 20,
                                          }}
                                        />
                                      </>
                                    )}
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: primaryBlue[600], mt: 0.5 }}
                                  >
                                    Qty: {item.selectedQuantity}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "right" }}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: primaryBlue[700],
                                      fontWeight: 700,
                                    }}
                                  >
                                    {formatPrice(item.totalWithGST || item.totalPrice)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      display: "block",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    (incl. GST)
                                  </Typography>
                                  {itemHasSavings && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#00BFFF",
                                        fontWeight: 600,
                                        display: "block",
                                      }}
                                    >
                                      Save{" "}
                                      {formatPrice(
                                        itemSavings * item.selectedQuantity
                                      )}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Regular Package Display */}
                      <Typography
                        variant={{ xs: "h5", sm: "h4" }}
                        sx={{ color: primaryBlue[600], fontWeight: 700, mb: 2 }}
                      >
                        {formatPrice(packagePrice)}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mb: 2, color: primaryBlue[600] }}
                      >
                        {packageData.description}
                      </Typography>

                      {packageData.features &&
                        packageData.features.length > 0 && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              gutterBottom
                              sx={{ color: primaryBlue[700], fontWeight: 600 }}
                            >
                              Package Features:
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              {packageData.features.map((feature, index) => (
                                <Typography
                                  key={index}
                                  variant="body2"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mb: 0.5,
                                    color: primaryBlue[600],
                                  }}
                                >
                                  • {feature}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Order Summary Table - Price Breakdown with Discount and GST */}
              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(15px)",
                  border: `1px solid ${primaryBlue[200]}`,
                  borderRadius: "16px",
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography
                    variant="h6"
                    sx={{ color: primaryBlue[700], fontWeight: 600, mb: 2 }}
                  >
                    Price Details
                  </Typography>

                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {/* Total MRP Row - Show original value */}
                        {hasSavings && originalPrice > 0 && (
                          <TableRow sx={{ bgcolor: "rgba(255, 152, 0, 0.08)" }}>
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                                py: 1.5,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: "#e65100" }}
                              >
                                Total MRP
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                Original price before discount
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                                py: 1.5,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "text.secondary",
                                  textDecoration: "line-through",
                                }}
                              >
                                {formatPrice(originalPrice)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Package Price Row */}
                        <TableRow>
                          <TableCell
                            sx={{
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {packageData.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {packageData.category}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {formatPrice(packagePrice)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Discount/Savings Row - Only show if there are savings */}
                        {hasSavings && (
                          <TableRow sx={{ bgcolor: "rgba(76, 175, 80, 0.08)" }}>
                            <TableCell
                              sx={{
                                py: 1.5,
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 600, color: "#2e7d32" }}
                              >
                                🎉 You Save
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                py: 1.5,
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 700, color: "#2e7d32" }}
                              >
                                {formatPrice(moneySaved)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#00BFFF",
                                  display: "block",
                                  fontWeight: 600,
                                }}
                              >
                                ({discountPercentage}% off)
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Items Subtotal Row - Without GST */}
                        {isSubItemsPackageWithGST && (
                          <TableRow>
                            <TableCell
                              sx={{
                                py: 1.5,
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                Items Subtotal
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                py: 1.5,
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                {formatPrice(itemsSubtotal)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* GST Row - Already Included (aggregated from all items) */}
                        {isSubItemsPackageWithGST && includedGSTAmount > 0 && (
                          <TableRow sx={{ bgcolor: "rgba(0, 114, 206, 0.03)" }}>
                            <TableCell
                              sx={{
                                py: 1.5,
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, color: primaryBlue[600] }}
                              >
                                GST
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", fontStyle: "italic" }}
                              >
                                Included in item prices (varies per item)
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                py: 1.5,
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, color: primaryBlue[600] }}
                              >
                                {formatPrice(includedGSTAmount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Subtotal Row - Items + GST */}
                        <TableRow>
                          <TableCell
                            sx={{
                              py: 1.5,
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              Subtotal {isSubItemsPackageWithGST ? "(incl. GST)" : ""}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              py: 1.5,
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {formatPrice(isSubItemsPackageWithGST ? itemsSubtotalWithGST : packagePrice)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Activation Fee Row */}
                        {activationFee > 0 && (
                          <TableRow sx={{ bgcolor: "rgba(76, 175, 80, 0.05)" }}>
                            <TableCell
                              sx={{
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                                py: 1.5,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, color: "#00BFFF" }}
                              >
                                Activation Fee
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                One-time activation charge
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                borderBottom: `1px solid ${primaryBlue[100]}`,
                                py: 1.5,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, color: "#00BFFF" }}
                              >
                                {formatPrice(activationFee)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Delivery Fee Row */}
                        <TableRow>
                          <TableCell
                            sx={{
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              Delivery Fee
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              borderBottom: `1px solid ${primaryBlue[100]}`,
                              py: 1.5,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {deliveryFee === 0 ? 'FREE' : formatPrice(deliveryFee)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Total Amount Row */}
                        <TableRow sx={{ bgcolor: primaryBlue[50] }}>
                          <TableCell sx={{ py: 2, borderBottom: "none" }}>
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 700 }}
                            >
                              Total Amount
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ py: 2, borderBottom: "none" }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, color: primaryBlue[700] }}
                            >
                              {formatPrice(totalAmount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>

            {/* Delivery Information - Only show for physical items */}
            {requiresDelivery && (
              <Box sx={{ mb: 4 }}>
                <Card
                  variant="outlined"
                  sx={{
                    background: "rgba(0, 114, 206, 0.1)",
                    backdropFilter: "blur(15px)",
                    border: `2px solid ${primaryBlue[200]}`,
                    borderRadius: "20px",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <LocalShipping sx={{ color: primaryBlue[600], mr: 1 }} />
                      <Typography
                        variant={{ xs: "h6", sm: "h6" }}
                        sx={{
                          color: primaryBlue[700],
                          fontWeight: 600,
                        }}
                      >
                        Delivery Information
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: primaryBlue[700],
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        📦 Delivery in 7–10 days
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: primaryBlue[600] }}
                      >
                        Your order will be delivered within 7 to 10 business days
                        from order confirmation
                      </Typography>
                    </Box>

                    {deliveryMessage.isFree ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CheckCircle
                          sx={{ color: "#00BFFF", mr: 1, fontSize: 20 }}
                        />
                        <Typography
                          variant="body1"
                          sx={{ color: "#00BFFF", fontWeight: 600 }}
                        >
                          {deliveryMessage.message}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[600], fontWeight: 500 }}
                        >
                          {deliveryMessage.message}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[500] }}
                        >
                          {deliveryMessage.additionalMessage}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[400], mt: 0.5 }}
                        >
                          Free shipping on orders above ₹2,999
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Pricing Breakdown - Only show if there are additional fees */}
            {needsRegistrationFee && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant={{ xs: "h6", sm: "h6" }}
                  gutterBottom
                  sx={{ color: primaryBlue[700], fontWeight: 600 }}
                >
                  Pricing Details
                </Typography>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    background: "rgba(255, 255, 255, 0.8)",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    size="small"
                    sx={{ minWidth: { xs: 280, sm: "auto" } }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Typography
                            variant="body1"
                            sx={{ color: primaryBlue[700] }}
                          >
                            {packageData.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {packageData.category} Package
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body1"
                            sx={{ color: primaryBlue[700], fontWeight: 600 }}
                          >
                            {formatPrice(packagePrice)}
                          </Typography>
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell colSpan={2}>
                          <Divider sx={{ bgcolor: primaryBlue[200] }} />
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell>
                          <Typography
                            variant="h6"
                            sx={{ color: primaryBlue[800], fontWeight: 700 }}
                          >
                            Total Amount
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant={{ xs: "h6", sm: "h5" }}
                            sx={{ color: "#00BFFF", fontWeight: 700 }}
                          >
                            {formatPrice(totalAmount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Cashfree Payment */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant={{ xs: "subtitle1", sm: "h6" }}
                gutterBottom
                sx={{
                  color: primaryBlue[700],
                  fontWeight: 600,
                  mb: { xs: 1, sm: 2 },
                }}
              >
                Payment Information
              </Typography>

              <Card
                variant="outlined"
                sx={{
                  bgcolor: "rgba(76, 175, 80, 0.1)",
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(15px)",
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                  borderRadius: "16px",
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  {USE_CASHFREE_PAYMENTS ? (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                        <PaymentIcon sx={{ color: "#00BFFF", mr: 1 }} />
                        <Typography
                          variant={{ xs: "subtitle1", sm: "h6" }}
                          sx={{ color: primaryBlue[700], fontWeight: 600 }}
                        >
                          Secure Payment via Cashfree
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "rgba(76, 175, 80, 0.1)",
                          p: { xs: 2, sm: 3 },
                          borderRadius: 2,
                          border: "2px solid #00BFFF",
                          textAlign: "center",
                          mb: 3,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ color: "#388e3c", fontWeight: 600, mb: 1 }}
                        >
                          Amount to Pay
                        </Typography>
                        <Typography
                          sx={{
                            color: "#00BFFF",
                            fontWeight: 700,
                            fontSize: { xs: "2rem", sm: "2.5rem" },
                          }}
                        >
                          {formatPrice(totalAmount)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          bgcolor: "rgba(255, 255, 255, 0.8)",
                          p: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          border: `1px solid ${primaryBlue[200]}`,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: primaryBlue[700], fontWeight: 500 }}
                        >
                          Pay securely using UPI, cards, net banking, or wallets
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "rgba(0, 26, 77, 0.04)",
                          border: `1px solid ${primaryBlue[200]}`,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ color: primaryBlue[700], fontWeight: 600, mb: 1.5 }}
                        >
                          Contact information
                        </Typography>
                        <MerchantLegalNotice variant="body2" />
                      </Box>
                    </>
                  ) : (
                    <ManualUpiPaymentBlock
                      amountLabel={formatPrice(totalAmount)}
                      transactionId={paymentTransactionId}
                      onTransactionIdChange={setPaymentTransactionId}
                      transactionIdError={transactionIdError}
                    />
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* Important Notes */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(15px)",
                border: "1px solid rgba(0, 114, 206, 0.3)",
                "& .MuiAlert-icon": {
                  color: primaryBlue[600],
                },
              }}
            >
              <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
                <strong>Important:</strong>{" "}
                {USE_CASHFREE_PAYMENTS
                  ? "After successful payment, your order is confirmed automatically."
                  : "After you pay and submit your transaction ID, an admin will verify payment and confirm your order. Benefits apply after confirmation."}
              </Typography>
            </Alert>

            {/* First Purchase Info for New Users */}
            {isAutoFillUpgrade && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(15px)",
                  border: "1px solid rgba(0, 114, 206, 0.3)",
                  "& .MuiAlert-icon": {
                    color: primaryBlue[600],
                  },
                }}
              >
                <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
                  <strong>Auto Filling Program upgrade:</strong> This payment
                  completes your activation fee upgrade (₹499 → ₹699). After
                  successful payment, ₹150 is added to your cashback wallet. Your
                  Ultra Customer status stays active; program features are
                  enabled as soon as payment succeeds.
                </Typography>
              </Alert>
            )}

            {!userProfile?.hasPurchasedPackage && !isAutoFillUpgrade && (
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  background: "rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(15px)",
                  border: "1px solid rgba(0, 114, 206, 0.3)",
                  "& .MuiAlert-icon": {
                    color: primaryBlue[600],
                  },
                }}
              >
                <Typography variant="body2" sx={{ color: primaryBlue[700] }}>
                  <strong>🎉 First Package Purchase:</strong> This purchase will
                  make you eligible for referral points on future orders!
                  {packageData?.category === "Activation Fee"
                    ? " You're purchasing the activation fee to unlock Points eligibility."
                    : " Package purchase includes activation benefits."}
                </Typography>
              </Alert>
            )}


            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                justifyContent: "center",
                mt: 4,
              }}
            >
              <Button
                variant="outlined"
                size="large"
                onClick={onBack}
                sx={{
                  minWidth: { xs: "100%", sm: 200 },
                  py: 1.5,
                  borderColor: primaryBlue[300],
                  color: primaryBlue[600],
                  fontWeight: 600,
                  bgcolor: "rgba(255, 255, 255, 0.7)",
                  "&:hover": {
                    borderColor: primaryBlue[500],
                    bgcolor: "rgba(255, 255, 255, 0.9)",
                  },
                }}
              >
                Back to Packages
              </Button>

              <Button
                variant="contained"
                size="large"
                onClick={handlePayment}
                startIcon={<PaymentIcon />}
                disabled={loading}
                sx={{
                  minWidth: { xs: "100%", sm: 200 },
                  py: 1.5,
                  background: `linear-gradient(45deg, #00BFFF, #60A5FA)`,
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
                  "&:hover": {
                    background: `linear-gradient(45deg, #388e3c, #00BFFF)`,
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
                  },
                  "&:disabled": {
                    background: "rgba(0, 0, 0, 0.12)",
                    color: "rgba(0, 0, 0, 0.5)",
                  },
                }}
              >
                {loading
                  ? "Processing..."
                  : USE_CASHFREE_PAYMENTS
                    ? "Pay with Cashfree"
                    : "I've paid — submit for approval"}
              </Button>
            </Box>
          </Paper>

        </Container>
      </Box>

      {/* Package Success Modal */}
      <PackageSuccessModal
        open={successModal}
        onClose={handleSuccessModalClose}
        onContinueShopping={handleContinueShopping}
        packageData={packageData}
        orderData={orderData}
        primaryBlue={primaryBlue}
      />
    </Box>
  );
};

export default CheckoutPage;
